import { Component, type ErrorInfo, type ReactNode } from 'react';
import { PageErrorPanel } from './PageErrorPanel';

interface Props {
  children: ReactNode;
  /** Changing this resets the boundary, so moving to another page clears an error. */
  resetKey: string;
  /** Where "Go to Home" leads. */
  homeTo: string;
  /** Renders the panel inside the shell, so the sidebar stays usable. */
  wrap?: (panel: ReactNode) => ReactNode;
}

interface State {
  error: Error | null;
}

/** Spec S00: an error boundary per page. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[page error]', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const panel = (
      <PageErrorPanel
        error={error}
        homeTo={this.props.homeTo}
        onRetry={() => this.setState({ error: null })}
      />
    );
    return this.props.wrap ? this.props.wrap(panel) : panel;
  }
}
